import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BACKEND_URL, formatAud } from "../../lib/api";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useCart } from "../../context/CartContext";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import { toast } from "sonner";
import {
  Truck, ShieldCheck, Package2, ChevronRight, Minus, Plus,
  Car, Tag, X, Phone, MessageSquare, ArrowRight, Check, ZoomIn,
} from "lucide-react";

// Proxy Pinnacle images through backend (avoids auth on img src)
const proxyImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http://api.carparts-au.com/")) {
    return `${BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
  }
  return url;
};

// Human-readable category from Pinnacle itemTypeCode e.g. FRONT_BUMPER → Front Bumper
const formatCategory = (code) => {
  if (!code) return "";
  return code.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

const TAG_STYLES = {
  "New Arrival": "bg-blue-100 text-blue-700 border-blue-200",
  "Clearance":   "bg-red-100 text-red-700 border-red-200",
  "Hot Deal":    "bg-orange-100 text-orange-700 border-orange-200",
  "Last One":    "bg-purple-100 text-purple-700 border-purple-200",
  "Low Stock":   "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Featured":    "bg-green-100 text-green-700 border-green-200",
};

// ─── Small ProductCard for related ───────────────────────────────────────
function SmallCard({ p }) {
  return (
    <Link to={`/product/${p.id}`}
      className="group border border-zinc-200 hover:border-[#1e5bb8] hover:shadow-md rounded-sm overflow-hidden transition-all">
      <div className="aspect-square bg-zinc-50 overflow-hidden">
        {p.image_url
          ? <img src={proxyImg(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400" />
          : <div className="w-full h-full flex items-center justify-center text-zinc-300 text-[11px] font-mono uppercase">No image</div>
        }
      </div>
      <div className="p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">{p.sku}</div>
        <div className="text-[13px] font-heading font-bold text-[#0b1a3b] line-clamp-2 leading-snug">{p.name}</div>
        {p.vehicle_make && <div className="text-[11px] text-zinc-500 mt-0.5">{p.vehicle_year} {p.vehicle_make}</div>}
        <div className="mt-2 font-heading font-black text-[15px] text-[#0b1a3b]">{formatAud(p.price)}</div>
      </div>
    </Link>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product,  setProduct]  = useState(null);
  const [settings, setSettings] = useState({ allow_backorder: false });
  const [qty,      setQty]      = useState(1);
  const [related,  setRelated]  = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [offerOpen, setOfferOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("description");
  const { addItem } = useCart();
  const { user, openLoginModal } = useCustomerAuth();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    setActiveTab("description");
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      if (r.data.category) {
        api.get(`/products?category=${encodeURIComponent(r.data.category)}&page_size=6`)
          .then((x) => setRelated((x.data.items || []).filter((p) => p.id !== r.data.id).slice(0, 4)));
      }
    });
  }, [id]);

  const gallery = useMemo(() => {
    if (!product) return [];
    // If admin has selected specific gallery images — use those
    if (product.gallery_images?.length) {
      const list = [...product.gallery_images];
      // Put featured_image first if not already included
      const main = product.featured_image || product.image_url;
      if (main && !list.includes(main)) list.unshift(main);
      return list;
    }
    // Default: all Pinnacle images (original behaviour)
    const list = product.images?.length ? [...product.images] : [];
    if (product.image_url && !list.includes(product.image_url)) list.unshift(product.image_url);
    return list.length ? list : [];
  }, [product]);

  if (!product) return (
    <div className="bg-white min-h-screen">
      <StoreHeader />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 text-center">
        <div className="inline-block w-8 h-8 border-2 border-[#1e5bb8] border-t-transparent rounded-full animate-spin mb-4" />
        <div className="text-zinc-400 font-mono text-[12px] uppercase tracking-widest">Loading part…</div>
      </div>
    </div>
  );

  const inStock              = (product.stock ?? 0) > 0;
  const isBackorderAvailable = !inStock && settings.allow_backorder;
  const canAddToCart         = inStock || isBackorderAvailable;
  const hasDonor             = !!(product.vehicle_make || product.vehicle_model || product.vehicle_year);

  // Negotiated price — set by server when logged-in customer has an active custom price
  const negotiatedPrice  = product.negotiated_price ?? null;
  const negotiatedExpiry = product.negotiated_expires_at ?? null;
  const hoursLeft = negotiatedExpiry
    ? Math.max(0, Math.round((new Date(negotiatedExpiry) - Date.now()) / 3600000))
    : null;

  // Build specs rows — only show rows with data
  const specs = [
    ["SKU / Part Number",  product.sku],
    ["Brand",              product.brand],
    ["Category",           formatCategory(product.category)],
    ["Condition",          (product.condition || "used").toUpperCase()],
    ...(product.barcode ? [["Barcode / OEM", product.barcode]] : []),
    ...(product.weight_kg ? [["Weight", `${product.weight_kg} kg`]] : []),
    ...(product.donor_engine ? [["Engine Code", product.donor_engine]] : []),
    ...(product.donor_vin ? [["Donor VIN", product.donor_vin]] : []),
  ].filter(([, v]) => v && v !== "—");

  // Interchange/fitment - placeholder for Pinnacle API
  // When Pinnacle OPS API is active, the product will contain an `interchange` array
  // from the Hollander Interchange database with compatible vehicle fitments.
  const interchange = product.interchange || [];  // populated by Pinnacle API

  const addToCart = () => {
    addItem(product, qty);
    toast.success(
      isBackorderAvailable ? `${product.name} added as backorder` : `${product.name} added to cart`,
      isBackorderAvailable ? { description: "We'll source this and contact you when ready.", duration: 5000 } : {}
    );
  };

  return (
    <div className="bg-white min-h-screen" data-testid="product-detail-page">
      <StoreHeader />

      {/* ── Breadcrumb ── */}
      <div className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-2 text-[12px] font-mono uppercase tracking-widest text-zinc-500 flex-wrap">
          <Link to="/" className="hover:text-[#0b1a3b] transition-colors">Home</Link>
          <ChevronRight className="h-3 w-3 text-zinc-300" />
          <Link to="/shop" className="hover:text-[#0b1a3b] transition-colors">All Parts</Link>
          {product.category && <>
            <ChevronRight className="h-3 w-3 text-zinc-300" />
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-[#0b1a3b] transition-colors">{formatCategory(product.category)}</Link>
          </>}
          <ChevronRight className="h-3 w-3 text-zinc-300" />
          <span className="text-zinc-800">{product.name}</span>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16">

          {/* ── LEFT: Gallery ── */}
          <div className="space-y-3" data-testid="pdp-gallery">

            {/* Main image */}
            <div className="relative bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden aspect-[4/3]" data-testid="pdp-gallery-main">
              {gallery.length > 0 && gallery[activeImg] ? (
                <img
                  src={proxyImg(gallery[activeImg])}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-zinc-300">
                  <Package2 className="h-16 w-16 mb-3" />
                  <div className="font-mono text-[11px] uppercase tracking-widest">No image available</div>
                </div>
              )}

              {/* Top badges */}
              <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                <span className={`px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest rounded-sm ${
                  product.condition === "damaged" ? "bg-[#e31e24] text-white"
                  : product.condition === "new"   ? "bg-green-600 text-white"
                  : "bg-[#0b1a3b] text-white"
                }`} data-testid="pdp-condition-badge">
                  {product.condition === "new" ? "New" : product.condition === "damaged" ? "Damaged" : "Used"}
                </span>
                {product.is_returned && (
                  <span className="px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest rounded-sm bg-amber-400 text-[#0b1a3b]">
                    Restocked
                  </span>
                )}
              </div>
              {!inStock && !isBackorderAvailable && (
                <div className="absolute top-3 right-3 bg-zinc-600 text-white text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-sm">
                  Sold
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {gallery.length > 1 && (
              <div className="grid grid-cols-5 gap-2" data-testid="pdp-gallery-thumbs">
                {gallery.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`aspect-square overflow-hidden rounded-sm border-2 transition-all ${i === activeImg ? "border-[#1e5bb8] ring-1 ring-[#1e5bb8]/20" : "border-zinc-200 hover:border-zinc-400"}`}
                    data-testid={`pdp-thumb-${i}`}>
                    <img src={proxyImg(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Trust row */}
            <div className="grid grid-cols-3 divide-x divide-zinc-200 border border-zinc-200 rounded-sm">
              <div className="p-3 flex items-center gap-2">
                <Truck className="h-4 w-4 text-[#e31e24] shrink-0" />
                <div>
                  <div className="font-heading font-bold text-[12px] uppercase text-[#0b1a3b]">Same-day ship</div>
                  <div className="text-[11px] text-zinc-500">Order before 2pm</div>
                </div>
              </div>
              <div className="p-3 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#e31e24] shrink-0" />
                <div>
                  <div className="font-heading font-bold text-[12px] uppercase text-[#0b1a3b]">Warranty</div>
                  <div className="text-[11px] text-zinc-500">30–90 days</div>
                </div>
              </div>
              <div className="p-3 flex items-center gap-2">
                <Package2 className="h-4 w-4 text-[#e31e24] shrink-0" />
                <div>
                  <div className="font-heading font-bold text-[12px] uppercase text-[#0b1a3b]">Returns</div>
                  <div className="text-[11px] text-zinc-500">30-day exchange</div>
                </div>
              </div>
            </div>

            {/* Need help? */}
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <div className="font-heading font-bold text-[13px] uppercase text-[#0b1a3b] mb-2">Need help with this part?</div>
              <div className="flex flex-wrap gap-2">
                <a href="tel:0406291986"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0b1a3b] text-white text-[12px] font-bold rounded-sm hover:bg-[#1e5bb8] transition-colors">
                  <Phone className="h-3.5 w-3.5" /> Call 0406 291 986
                </a>
                <Link to="/quote"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#1e5bb8] text-[#1e5bb8] text-[12px] font-bold rounded-sm hover:bg-[#1e5bb8] hover:text-white transition-colors">
                  <MessageSquare className="h-3.5 w-3.5" /> Request a quote
                </Link>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Info ── */}
          <div>
            {/* Meta */}
            <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">
              <span>{product.brand}</span>
              <span className="text-zinc-200">·</span>
              <span className="text-[#1e5bb8]">SKU: {product.sku}</span>
              {product.category && <>
                <span className="text-zinc-200">·</span>
                <Link to={`/shop?category=${encodeURIComponent(product.category)}`}
                  className="text-zinc-400 hover:text-[#1e5bb8] transition-colors">{formatCategory(product.category)}</Link>
              </>}
            </div>

            {/* Title */}
            <h1 className="font-heading text-[30px] md:text-[36px] font-black uppercase text-[#0b1a3b] leading-tight mb-4" data-testid="product-title">
              {product.name}
            </h1>

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {product.tags.map((tag) => (
                  <span key={tag} className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 rounded-sm border ${TAG_STYLES[tag] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Donor vehicle */}
            <div className={`mb-5 p-4 rounded-sm border-l-4 ${hasDonor ? "border-[#1e5bb8] bg-blue-50" : "border-zinc-300 bg-zinc-50"}`} data-testid="pdp-donor-card">
              <div className="flex items-center gap-2 mb-2">
                <Car className={`h-4 w-4 shrink-0 ${hasDonor ? "text-[#1e5bb8]" : "text-zinc-400"}`} />
                <div className={`font-heading text-[13px] font-black uppercase tracking-wide ${hasDonor ? "text-[#0b1a3b]" : "text-zinc-500"}`}>
                  {hasDonor ? "Removed from" : "Universal / Aftermarket"}
                </div>
              </div>
              {hasDonor ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[13px]">
                  {product.vehicle_year  && <div><span className="text-zinc-500">Year</span> · <strong>{product.vehicle_year}</strong></div>}
                  {product.vehicle_make  && <div><span className="text-zinc-500">Make</span> · <strong>{product.vehicle_make}</strong></div>}
                  {product.vehicle_model && <div><span className="text-zinc-500">Model</span> · <strong>{product.vehicle_model}</strong></div>}
                  {product.donor_engine  && <div><span className="text-zinc-500">Engine</span> · <strong>{product.donor_engine}</strong></div>}
                  {product.donor_vin     && <div className="col-span-2"><span className="text-zinc-500">VIN</span> · <span className="font-mono text-[11px]">{product.donor_vin}</span></div>}
                </div>
              ) : (
                <div className="text-[13px] text-zinc-500">Suits multiple makes and models. Check fitment before ordering or call us.</div>
              )}
            </div>

            {/* Price */}
            <div className="mb-2">
              {negotiatedPrice ? (
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-2"
                    style={{ background: "#dcfce7", border: "1px solid #86efac" }}>
                    <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-green-700">
                      ✓ Your negotiated price · expires in {hoursLeft}h
                    </span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <div className="font-heading text-[42px] font-black text-green-700 leading-none" data-testid="product-price">
                      {formatAud(negotiatedPrice)}
                    </div>
                    <div className="text-[14px] font-mono text-zinc-400 line-through">{formatAud(product.price)}</div>
                    <div className="text-[12px] font-mono uppercase tracking-widest text-zinc-400">incl. GST</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-baseline gap-3">
                  <div className="font-heading text-[42px] font-black text-[#0b1a3b] leading-none" data-testid="product-price">
                    {formatAud(product.price)}
                  </div>
                  <div className="text-[12px] font-mono uppercase tracking-widest text-zinc-400">incl. GST</div>
                </div>
              )}
            </div>

            {/* Stock status */}
            <div className={`text-[13px] font-mono uppercase tracking-widest font-bold mb-1 ${inStock ? "text-green-600" : isBackorderAvailable ? "text-amber-600" : "text-zinc-500"}`}>
              {inStock
                ? `✓ In stock · ${product.stock} available`
                : isBackorderAvailable ? "⚠ Out of stock · backorder accepted"
                : "✕ Out of stock"}
            </div>
            {isBackorderAvailable && (
              <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-4">
                <strong>Backorder:</strong> We'll source this part and ship as soon as available. You'll be contacted before any further charge.
              </div>
            )}

            {/* Add to cart */}
            <div className="flex items-stretch gap-2 mt-5 mb-6">
              <div className="flex border-2 border-zinc-300 rounded-sm overflow-hidden">
                <button onClick={() => setQty((n) => Math.max(1, n - 1))}
                  className="w-11 h-12 flex items-center justify-center hover:bg-zinc-100 transition-colors" data-testid="qty-decrement">
                  <Minus className="h-4 w-4" />
                </button>
                <div className="w-12 flex items-center justify-center font-heading font-bold text-[16px] border-x-2 border-zinc-300" data-testid="qty-value">{qty}</div>
                <button onClick={() => setQty((n) => n + 1)}
                  className="w-11 h-12 flex items-center justify-center hover:bg-zinc-100 transition-colors" data-testid="qty-increment">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={addToCart}
                disabled={!canAddToCart}
                className={`flex-1 h-12 rounded-sm font-heading font-black uppercase tracking-wide text-[15px] transition-colors flex items-center justify-center gap-2 ${
                  !canAddToCart ? "bg-zinc-200 text-zinc-400 cursor-not-allowed"
                  : isBackorderAvailable ? "bg-amber-500 hover:bg-amber-600 text-white"
                  : "bg-[#e31e24] hover:bg-[#b71820] text-white"
                }`}
                data-testid="product-add-to-cart">
                {canAddToCart && <Check className="h-4 w-4" />}
                {isBackorderAvailable ? "Add as Backorder" : canAddToCart ? "Add to Cart" : "Out of Stock"}
              </button>
              {product.is_negotiable && (
                user ? (
                  <button
                    onClick={() => setOfferOpen(true)}
                    className="h-12 px-4 rounded-sm border-2 border-[#1e5bb8] text-[#1e5bb8] hover:bg-[#1e5bb8] hover:text-white font-heading font-black uppercase tracking-wide text-[13px] transition-colors flex items-center gap-1.5 whitespace-nowrap"
                    data-testid="product-make-offer">
                    <Tag className="h-4 w-4" /> Make Offer
                  </button>
                ) : (
                  <button
                    onClick={() => openLoginModal(window.location.pathname)}
                    className="h-12 px-4 rounded-sm border-2 border-[#1e5bb8] text-[#1e5bb8] hover:bg-[#1e5bb8] hover:text-white font-heading font-black uppercase tracking-wide text-[13px] transition-colors flex items-center gap-1.5 whitespace-nowrap"
                    data-testid="product-make-offer-login">
                    <Tag className="h-4 w-4" /> Sign in to Offer
                  </button>
                )
              )}
            </div>

            {/* Tabs */}
            <div className="border-b border-zinc-200 flex gap-0 mb-5">
              {[
                { id: "description", label: "Description" },
                { id: "specs",       label: "Specifications" },
                { id: "fitment",     label: "Fitment / Interchange" },
              ].map((t) => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`px-4 py-2.5 text-[13px] font-heading font-bold uppercase tracking-wide border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === t.id
                      ? "border-[#e31e24] text-[#0b1a3b]"
                      : "border-transparent text-zinc-500 hover:text-[#0b1a3b]"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab: Description */}
            {activeTab === "description" && (
              <div className="prose prose-sm max-w-none text-zinc-700 leading-relaxed">
                {product.long_description || product.description ? (
                  <p className="text-[14px] leading-relaxed">{product.long_description || product.description}</p>
                ) : (
                  <div className="text-[14px] text-zinc-500">
                    <p className="mb-3">
                      <strong className="text-[#0b1a3b]">{product.name}</strong> — a quality
                      {product.condition === "new" ? " new " : " used "}
                      {formatCategory(product.category)?.toLowerCase()} part{hasDonor ? ` removed from a ${product.vehicle_year} ${product.vehicle_make} ${product.vehicle_model}` : ""}.
                    </p>
                    <p>All parts are inspected before sale and backed by our 30–90 day warranty. Same-day dispatch available on orders placed before 2pm (AEST).</p>
                    <p className="mt-3 text-[12px] text-zinc-400">
                      Note: Full part description will be available when Pinnacle API is connected.
                      Contact us for more details: <a href="tel:0406291986" className="text-[#1e5bb8]">0406 291 986</a>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Specifications */}
            {activeTab === "specs" && (
              <table className="w-full text-[13px] border border-zinc-200 rounded-sm overflow-hidden">
                <tbody className="divide-y divide-zinc-100">
                  {specs.map(([k, v]) => (
                    <tr key={k} className="even:bg-zinc-50">
                      <td className="px-4 py-2.5 w-44 font-mono text-[11px] uppercase tracking-widest text-zinc-500 font-medium">{k}</td>
                      <td className="px-4 py-2.5 text-zinc-800 font-medium">{v}</td>
                    </tr>
                  ))}
                  {specs.length === 0 && (
                    <tr><td colSpan={2} className="px-4 py-6 text-center text-zinc-400 text-[13px]">No specifications available yet.</td></tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Tab: Fitment / Interchange */}
            {activeTab === "fitment" && (
              <div>
                {interchange.length > 0 ? (
                  <div>
                    <p className="text-[13px] text-zinc-600 mb-4">This part fits the following vehicles (Hollander Interchange data):</p>
                    <table className="w-full text-[13px] border border-zinc-200 rounded-sm overflow-hidden">
                      <thead className="bg-[#0b1a3b] text-white">
                        <tr>
                          <th className="px-4 py-2 text-left font-mono uppercase text-[10px] tracking-widest">Year</th>
                          <th className="px-4 py-2 text-left font-mono uppercase text-[10px] tracking-widest">Make</th>
                          <th className="px-4 py-2 text-left font-mono uppercase text-[10px] tracking-widest">Model</th>
                          <th className="px-4 py-2 text-left font-mono uppercase text-[10px] tracking-widest">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {interchange.map((row, i) => (
                          <tr key={i} className="even:bg-zinc-50">
                            <td className="px-4 py-2">{row.year || "—"}</td>
                            <td className="px-4 py-2 font-medium">{row.make || "—"}</td>
                            <td className="px-4 py-2">{row.model || "—"}</td>
                            <td className="px-4 py-2 text-zinc-500">{row.notes || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="border border-zinc-200 rounded-sm p-6 text-center">
                    {hasDonor ? (
                      <div>
                        <div className="font-heading font-bold text-[16px] uppercase text-[#0b1a3b] mb-2">Removed from:</div>
                        <div className="text-[20px] font-heading font-black text-[#1e5bb8]">
                          {product.vehicle_year} {product.vehicle_make} {product.vehicle_model}
                        </div>
                        {product.donor_engine && <div className="text-[13px] text-zinc-500 mt-1">Engine: {product.donor_engine}</div>}
                        <div className="mt-4 text-[13px] text-zinc-500">
                          Cross-vehicle fitment data (Hollander Interchange) will be available once Pinnacle API is connected.
                          Call us to check if this part suits your vehicle.
                        </div>
                      </div>
                    ) : (
                      <div className="text-[14px] text-zinc-500">
                        <div className="font-heading font-bold text-[#0b1a3b] mb-2">Universal / Aftermarket Part</div>
                        <p>This is an aftermarket part. Please verify fitment for your specific vehicle.</p>
                        <p className="mt-2">Hollander Interchange data will populate here once Pinnacle API is connected.</p>
                      </div>
                    )}
                    <a href="tel:0406291986"
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#0b1a3b] text-white text-[13px] font-bold rounded-sm hover:bg-[#1e5bb8] transition-colors">
                      <Phone className="h-3.5 w-3.5" /> Call to confirm fitment
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Related parts ── */}
        {related.length > 0 && (
          <section className="mt-16 pt-10 border-t border-zinc-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-[22px] font-black uppercase text-[#0b1a3b]">Related Parts</h2>
              {product.category && (
                <Link to={`/shop?category=${encodeURIComponent(product.category)}`}
                  className="flex items-center gap-1.5 text-[13px] font-bold text-[#1e5bb8] hover:text-[#0b3ea0] uppercase tracking-wide">
                  More {formatCategory(product.category)} <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((p) => <SmallCard key={p.id} p={p} />)}
            </div>
          </section>
        )}
      </div>

      <StoreFooter />
      {offerOpen && <OfferModal product={product} onClose={() => setOfferOpen(false)} />}
    </div>
  );
}

function OfferModal({ product, onClose }) {
  const { user } = useCustomerAuth();
  const [form, setForm] = useState({
    offered_price: Math.round(product.price * 0.85),
    name:  user?.name  || "",
    email: user?.email || "",
    phone: user?.phone || "",
    note:  "",
  });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/offers", {
        product_id: product.id, ...form,
        offered_price: Number(form.offered_price),
      });
      setDone(data);
      toast.success("Offer submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit offer");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="offer-modal">
      <div className="bg-white rounded-sm w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Make an offer</div>
            <div className="font-heading text-[18px] font-black uppercase text-[#0b1a3b] line-clamp-2">{product.name}</div>
            <div className="text-[12px] text-zinc-500 font-mono">List price · {formatAud(product.price)}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 ml-3">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="border border-green-200 bg-green-50 rounded-sm p-5 text-center" data-testid="offer-success">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div className="font-heading font-black uppercase text-[#0b1a3b] mb-1">Offer received!</div>
            <div className="text-[13px] text-zinc-600">
              Reference <strong className="font-mono">{done.offer_number}</strong>. We'll respond within 1 business day to <strong>{done.email}</strong>.
            </div>
            <button onClick={onClose} className="mt-4 px-5 py-2 border border-zinc-300 text-zinc-700 text-[13px] font-bold rounded-sm hover:border-zinc-400">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-[12px] font-bold uppercase text-zinc-600">Your offer (AUD) *</Label>
              <Input type="number" min="1" step="0.01" required value={form.offered_price}
                onChange={(e) => setForm({ ...form, offered_price: e.target.value })}
                className="mt-1 rounded-sm h-10 text-[15px] font-bold" data-testid="offer-price" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] font-bold uppercase text-zinc-600">Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="offer-name" />
              </div>
              <div>
                <Label className="text-[12px] font-bold uppercase text-zinc-600">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" data-testid="offer-phone" />
              </div>
            </div>
            <div>
              <Label className="text-[12px] font-bold uppercase text-zinc-600">Email *</Label>
              <Input type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                readOnly={!!user?.email}
                className={`mt-1 rounded-sm ${user?.email ? "bg-zinc-50 text-zinc-500" : ""}`}
                data-testid="offer-email" />
              {user?.email && <div className="text-[11px] text-zinc-400 mt-0.5">Logged in as {user.email}</div>}
            </div>
            <div>
              <Label className="text-[12px] font-bold uppercase text-zinc-600">Note (optional)</Label>
              <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-[13px] border border-zinc-300 rounded-sm focus:outline-none focus:border-[#1e5bb8]"
                placeholder="Bulk order, urgent, fitment query…" />
            </div>
            <button type="submit" disabled={busy}
              className="w-full h-11 rounded-sm bg-[#e31e24] hover:bg-[#b71820] text-white font-heading font-black uppercase tracking-wide text-[14px] transition-colors disabled:opacity-60"
              data-testid="offer-submit">
              {busy ? "Submitting…" : "Send Offer"}
            </button>
            <div className="text-[11px] text-zinc-400 text-center">
              Submitting an offer doesn't reserve stock. Accepted offers receive a 7-day quoted price link valid only for you.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}